import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts';
import {
  AlertTriangle, CheckCircle, Info, FileText, Activity,
  TrendingUp, ArrowUpRight, ArrowDownLeft, XCircle, ChevronDown, ChevronRight,
} from 'lucide-react';

const PATTERN_COLORS = {
  STRUCTURING: '#ef4444', CONCENTRATOR: '#f97316', DISPERSER: '#eab308',
  HIGH_VELOCITY: '#ec4899', CIRCULAR: '#dc2626', SMURFING: '#8b5cf6',
  LAYERING: '#6366f1', DORMANT_ACTIVATION: '#14b8a6', ROUND_AMOUNTS: '#f59e0b',
  OFF_HOURS: '#64748b'
};

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);
};

export const InvestigacionIndividualTab = ({
  resultados, clientData, selectedClient, setSelectedClient,
  showClientNames, setShowClientNames,
  showNoDataClients, setShowNoDataClients,
  showDuplicates, setShowDuplicates,
  showWithDataClients, setShowWithDataClients,
  selectedPattern, setSelectedPattern,
}) => (
  <div className="space-y-4">
    {/* Selector de cliente con toggle */}
    {resultados?.analisis_individual?.length > 1 && (
      <Card className="p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-700">Clientes Investigados:</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Mostrar:</span>
            <Button variant={showClientNames ? "outline" : "default"} size="sm"
              onClick={() => setShowClientNames(false)}
              className={!showClientNames ? "bg-purple-600 text-white hover:bg-purple-700" : ""}>CVU</Button>
            <Button variant={showClientNames ? "default" : "outline"} size="sm"
              onClick={() => setShowClientNames(true)}
              className={showClientNames ? "bg-purple-600 text-white hover:bg-purple-700" : ""}>Nombre</Button>
          </div>
        </div>

        {/* Detectar clientes duplicados */}
        {(() => {
          const duplicates = {};
          resultados.analisis_individual.forEach(client => {
            const cuit = client.perfil?.cuit;
            const nombre = client.perfil?.denominacion || client.perfil?.razon_social;
            const key = cuit || nombre;
            if (key) {
              if (!duplicates[key]) duplicates[key] = [];
              duplicates[key].push({
                cvu: client.cliente_id, nombre, cuit,
                txs: client.metricas?.total_transacciones || 0,
                volumen: client.metricas?.volumen_total || 0
              });
            }
          });
          const duplicateGroups = Object.entries(duplicates).filter(([_, items]) => items.length > 1);

          if (duplicateGroups.length > 0) {
            return (
              <div className="mb-4 border border-amber-300 rounded-lg overflow-hidden">
                <button onClick={() => setShowDuplicates(!showDuplicates)}
                  className="w-full flex items-center justify-between p-3 bg-amber-50 hover:bg-amber-100 transition-colors">
                  <div className="flex items-center gap-2 text-amber-700 font-medium text-sm">
                    <AlertTriangle size={16} />
                    <span>Clientes con múltiples CVUs ({duplicateGroups.length} titular{duplicateGroups.length > 1 ? 'es' : ''})</span>
                  </div>
                  {showDuplicates ? <ChevronDown size={16} className="text-amber-600" /> : <ChevronRight size={16} className="text-amber-600" />}
                </button>
                {showDuplicates && (
                  <div className="bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50 border-b border-amber-200">
                        <tr>
                          <th className="text-left px-3 py-2 text-amber-700 font-semibold">Titular</th>
                          <th className="text-left px-3 py-2 text-amber-700 font-semibold">CUIT</th>
                          <th className="text-left px-3 py-2 text-amber-700 font-semibold">CVU/CBU</th>
                          <th className="text-right px-3 py-2 text-amber-700 font-semibold">Txs</th>
                          <th className="text-right px-3 py-2 text-amber-700 font-semibold">Volumen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicateGroups.map(([key, items]) => (
                          items.map((item, idx) => (
                            <tr key={`${key}-${idx}`}
                              className={`border-b border-amber-100 ${idx === 0 ? 'bg-amber-50/50' : ''} hover:bg-amber-50`}>
                              {idx === 0 ? (
                                <td className="px-3 py-2 font-medium text-slate-700" rowSpan={items.length}>
                                  {item.nombre || 'Sin nombre'}
                                  <span className="block text-amber-600 text-[10px] mt-0.5">{items.length} cuentas</span>
                                </td>
                              ) : null}
                              {idx === 0 ? (
                                <td className="px-3 py-2 text-slate-500 font-mono" rowSpan={items.length}>
                                  {item.cuit || '-'}
                                </td>
                              ) : null}
                              <td className="px-3 py-2 text-slate-600 font-mono">{item.cvu}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.txs.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-slate-700">
                                ${item.volumen.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          }
          return null;
        })()}

        {/* Clientes CON/SIN transacciones */}
        {(() => {
          const clientsWithData = resultados.analisis_individual.filter(c => (c.metricas?.total_transacciones || 0) > 0);
          const clientsWithoutData = resultados.analisis_individual.filter(c => (c.metricas?.total_transacciones || 0) === 0);
          return (
            <>
              {clientsWithData.length > 0 && (
                <div className="mb-3 border border-green-200 rounded-lg overflow-hidden">
                  <button onClick={() => setShowWithDataClients(!showWithDataClients)}
                    className="w-full flex items-center justify-between p-2 bg-green-50 hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-2 text-green-700 text-xs font-medium">
                      <CheckCircle size={14} /><span>Con transacciones ({clientsWithData.length})</span>
                    </div>
                    {showWithDataClients ? <ChevronDown size={14} className="text-green-600" /> : <ChevronRight size={14} className="text-green-600" />}
                  </button>
                  {showWithDataClients && (
                    <div className="p-2 bg-white flex flex-wrap gap-2">
                      {clientsWithData.map((client) => {
                        const isSelected = selectedClient === client.cliente_id;
                        const displayText = showClientNames
                          ? (client.perfil?.denominacion || client.perfil?.razon_social || 'Sin nombre')
                          : (client.cliente_id.length > 15
                            ? `${client.cliente_id.substring(0, 8)}...${client.cliente_id.substring(client.cliente_id.length - 4)}`
                            : client.cliente_id);
                        return (
                          <Button key={client.cliente_id}
                            variant={isSelected ? "default" : "outline"} size="sm"
                            onClick={() => setSelectedClient(client.cliente_id)}
                            className={`${isSelected
                              ? 'bg-purple-600 text-white hover:bg-purple-700 border-purple-600'
                              : 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100'
                            }`}
                            title={`${client.cliente_id}\n${client.perfil?.denominacion || 'Sin nombre'}\nTxs: ${client.metricas?.total_transacciones || 0}`}>
                            {!isSelected && <CheckCircle size={12} className="mr-1 text-green-600" />}
                            {showClientNames && displayText.length > 18 ? displayText.substring(0, 18) + '...' : displayText}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {clientsWithoutData.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button onClick={() => setShowNoDataClients(!showNoDataClients)}
                    className="w-full flex items-center justify-between p-2 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-medium">
                      <XCircle size={14} /><span>Sin transacciones ({clientsWithoutData.length})</span>
                    </div>
                    {showNoDataClients ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                  </button>
                  {showNoDataClients && (
                    <div className="p-2 bg-white flex flex-wrap gap-2">
                      {clientsWithoutData.map((client) => {
                        const displayText = showClientNames
                          ? (client.perfil?.denominacion || client.perfil?.razon_social || 'Sin nombre')
                          : (client.cliente_id.length > 15
                            ? `${client.cliente_id.substring(0, 8)}...${client.cliente_id.substring(client.cliente_id.length - 4)}`
                            : client.cliente_id);
                        return (
                          <Button key={client.cliente_id} variant="outline" size="sm"
                            onClick={() => setSelectedClient(client.cliente_id)}
                            className="border-slate-300 text-slate-400 bg-slate-50 hover:bg-slate-100"
                            title={`${client.cliente_id}\n${client.perfil?.denominacion || 'Sin nombre'}\nTxs: 0`}>
                            {showClientNames && displayText.length > 18 ? displayText.substring(0, 18) + '...' : displayText}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </Card>
    )}

    {clientData && (
      <>
        {/* Perfil y Métricas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="p-4 bg-white">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <FileText size={16} />Perfil del Cliente
            </h3>
            <div className="space-y-2">
              <div><span className="text-xs text-slate-500">Identificador</span>
                <p className="font-mono text-sm font-medium">{clientData.cliente_id}</p></div>
              <div><span className="text-xs text-slate-500">Tipo</span>
                <p className="text-sm">{clientData.tipo_identificador}</p></div>
              {clientData.cvu_analizado && clientData.cvu_analizado !== clientData.cliente_id && (
                <div><span className="text-xs text-slate-500">CVU Asociado</span>
                  <p className="text-sm font-mono">{clientData.cvu_analizado}</p></div>
              )}
              {clientData.perfil && (
                <>
                  <div className="pt-2 border-t"><span className="text-xs text-slate-500">Denominación</span>
                    <p className="text-sm font-semibold text-purple-700">{clientData.perfil.denominacion || '-'}</p></div>
                  {clientData.perfil.razon_social && (
                    <div><span className="text-xs text-slate-500">Razón Social</span>
                      <p className="text-sm">{clientData.perfil.razon_social}</p></div>
                  )}
                  {clientData.perfil.nombre && (
                    <div><span className="text-xs text-slate-500">Nombre</span>
                      <p className="text-sm">{clientData.perfil.nombre} {clientData.perfil.apellido || ''}</p></div>
                  )}
                  <div><span className="text-xs text-slate-500">CUIT</span>
                    <p className="text-sm font-mono">{clientData.perfil.cuit || '-'}</p></div>
                </>
              )}
              {clientData.risk_score > 0 && (
                <div className="pt-2 border-t"><span className="text-xs text-slate-500">Puntaje de Riesgo</span>
                  <div className="flex items-center gap-2">
                    <p className={`text-lg font-bold ${
                      clientData.risk_score >= 70 ? 'text-red-600' :
                      clientData.risk_score >= 40 ? 'text-amber-600' : 'text-green-600'
                    }`}>{clientData.risk_score}</p>
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${
                        clientData.risk_score >= 70 ? 'bg-red-500' :
                        clientData.risk_score >= 40 ? 'bg-amber-500' : 'bg-green-500'
                      }`} style={{ width: `${clientData.risk_score}%` }} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-4 bg-white lg:col-span-2">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Activity size={16} />Métricas del Período
            </h3>
            {clientData.metricas && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-900">{clientData.metricas.total_transacciones?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-500">Transacciones</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-600">{formatMoney(clientData.metricas.volumen_total)}</p>
                  <p className="text-xs text-slate-500">Volumen Total</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{formatMoney(clientData.metricas.volumen_entradas)}</p>
                  <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><ArrowDownLeft size={12} /> Entradas</p>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{formatMoney(clientData.metricas.volumen_salidas)}</p>
                  <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><ArrowUpRight size={12} /> Salidas</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-slate-900">{formatMoney(clientData.metricas.promedio)}</p>
                  <p className="text-xs text-slate-500">Promedio/Tx</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-slate-900">{clientData.metricas.dias_activos || 0}</p>
                  <p className="text-xs text-slate-500">Días Activos</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-blue-600">{clientData.metricas.origenes_distintos || 0}</p>
                  <p className="text-xs text-slate-500">Orígenes</p>
                </div>
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-lg font-bold text-amber-600">{clientData.metricas.destinos_distintos || 0}</p>
                  <p className="text-xs text-slate-500">Destinos</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Evolución temporal y Patrones */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 bg-white">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <TrendingUp size={16} />Evolución Temporal
            </h3>
            <div style={{ height: 250 }}>
              {clientData.evolucion_temporal?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={clientData.evolucion_temporal}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10 }} tickFormatter={(v) => v.substring(5)} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(v) => formatMoney(v)} labelFormatter={(l) => `Fecha: ${l}`} />
                    <Legend />
                    <Area type="monotone" dataKey="entradas" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.5} name="Entradas" />
                    <Area type="monotone" dataKey="salidas" stackId="2" stroke="#ef4444" fill="#ef4444" fillOpacity={0.5} name="Salidas" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">Sin datos de evolución</div>
              )}
            </div>
          </Card>

          <Card className="p-4 bg-white">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} />Patrones Detectados
              {clientData.patrones_detectados?.length > 0 && (
                <Badge className="bg-red-100 text-red-700 ml-auto">{clientData.patrones_detectados.length} patrones</Badge>
              )}
            </h3>
            <div className="space-y-2 max-h-[230px] overflow-y-auto">
              {clientData.patrones_detectados?.length > 0 ? (
                clientData.patrones_detectados.map((pattern, idx) => (
                  <div key={idx} className="p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-shadow"
                    style={{
                      borderLeftColor: PATTERN_COLORS[pattern.type] || '#64748b',
                      backgroundColor: selectedPattern === idx ? '#f1f5f9' : '#f8fafc'
                    }}
                    onClick={() => setSelectedPattern(selectedPattern === idx ? null : idx)}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{pattern.name}</span>
                      {pattern.severity && (
                        <Badge className={
                          pattern.severity === 'high' ? 'bg-red-100 text-red-700' :
                          pattern.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                        }>
                          {pattern.severity === 'high' ? 'Alta' : pattern.severity === 'medium' ? 'Media' : 'Baja'}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{pattern.description}</p>
                    {selectedPattern === idx && (
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                        {pattern.details && (
                          <div className="text-xs text-slate-600 font-mono bg-slate-100 p-2 rounded">
                            <Info size={12} className="inline mr-1" />{pattern.details}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {pattern.count && (
                            <div className="p-2 bg-purple-50 rounded">
                              <span className="text-slate-500">Ocurrencias:</span>
                              <p className="font-bold text-purple-700">{pattern.count.toLocaleString()}</p>
                            </div>
                          )}
                          {pattern.amount && (
                            <div className="p-2 bg-green-50 rounded">
                              <span className="text-slate-500">Monto Total:</span>
                              <p className="font-bold text-green-700">{formatMoney(pattern.amount)}</p>
                            </div>
                          )}
                          {pattern.avg_amount && (
                            <div className="p-2 bg-blue-50 rounded">
                              <span className="text-slate-500">Monto Promedio:</span>
                              <p className="font-bold text-blue-700">{formatMoney(pattern.avg_amount)}</p>
                            </div>
                          )}
                          {pattern.threshold && (
                            <div className="p-2 bg-amber-50 rounded">
                              <span className="text-slate-500">Umbral:</span>
                              <p className="font-bold text-amber-700">{pattern.threshold}</p>
                            </div>
                          )}
                        </div>
                        {pattern.examples && pattern.examples.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs font-medium text-slate-600 mb-1">Ejemplos de transacciones:</p>
                            <div className="space-y-1 max-h-24 overflow-y-auto">
                              {pattern.examples.slice(0, 5).map((ex) => (
                                <div key={`${ex.fecha}-${ex.monto}`} className="text-xs bg-slate-100 p-1 rounded flex justify-between">
                                  <span className="text-slate-500">{ex.fecha || 'N/A'}</span>
                                  <span className="font-mono">{formatMoney(ex.monto)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {pattern.min_amount !== undefined && pattern.max_amount !== undefined && (
                          <div className="p-2 bg-red-50 rounded text-xs">
                            <p className="text-slate-600">
                              <strong>Rango detectado:</strong> {formatMoney(pattern.min_amount)} - {formatMoney(pattern.max_amount)}
                            </p>
                          </div>
                        )}
                        <div className="text-xs text-purple-600 mt-2 pt-2 border-t">
                          <strong>Tipo de patrón:</strong> {pattern.type}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
                  <p>No se detectaron patrones sospechosos</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Top Contrapartes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4 bg-white">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <ArrowDownLeft size={16} className="text-green-600" />Top Orígenes (Entradas)
            </h3>
            <div className="space-y-2">
              {clientData.top_contrapartes?.entradas?.length > 0 ? (
                clientData.top_contrapartes.entradas.slice(0, 5).map((cp, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{cp.nombre}</p>
                      <p className="text-xs text-slate-400 font-mono truncate">{cp.cuenta}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-sm font-bold text-green-600">{formatMoney(cp.volumen)}</p>
                      <p className="text-xs text-slate-400">{cp.cantidad} txs</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>
              )}
            </div>
          </Card>
          <Card className="p-4 bg-white">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <ArrowUpRight size={16} className="text-red-600" />Top Destinos (Salidas)
            </h3>
            <div className="space-y-2">
              {clientData.top_contrapartes?.salidas?.length > 0 ? (
                clientData.top_contrapartes.salidas.slice(0, 5).map((cp, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{cp.nombre}</p>
                      <p className="text-xs text-slate-400 font-mono truncate">{cp.cuenta}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-sm font-bold text-red-600">{formatMoney(cp.volumen)}</p>
                      <p className="text-xs text-slate-400">{cp.cantidad} txs</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Sin datos</p>
              )}
            </div>
          </Card>
        </div>
      </>
    )}
  </div>
);

export default InvestigacionIndividualTab;
